import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import { GET } from "@/services/activity-requests";
import { Question } from "@/types/activity";
import { toast } from "sonner";
import { removeMultipleKeys } from "@/utils/function";
import { tokenRefresh } from "@/redux/Refresh";

interface ActivitiesState {
  data: Question[] | null;
  loading: boolean;
  error: string | null;
}

interface FetchActivitiesArgs {
  token: string;
  level_id: string | undefined;
  lesson_id: string | undefined;
  language: string | undefined;
}

const initialState: ActivitiesState = {
  data: null,
  loading: false,
  error: null,
};

export const activitiesFetch = createAsyncThunk<
  Question[],
  FetchActivitiesArgs,
  { rejectValue: string }
>("activity-activities", async ({ token, level_id, lesson_id, language }, { rejectWithValue }) => {
  try {
    const { response } = await GET(
      `/interactive-lessons/${language}/levels/${level_id}/lessons/${lesson_id}/activities`,
      token
    );

    return response?.data?.result;
  } catch (err: unknown) {
    const error = err as any;
    if (error.response?.status === 401) {
      removeMultipleKeys(["pluri_token", "pluri_refresh_token", "user_detail"]);
      toast.error("Session expired. Please log in again.", {
        id: "session-expired-toast",
        richColors: true,
      });
      window.location.href = "/login";
    }
    const errorMessage =
      error.response?.data?.message || "An error occurred while fetching the profile picture";
    return rejectWithValue(errorMessage);
  }
});

const activitiesSlice = createSlice({
  name: "activities",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(activitiesFetch.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        activitiesFetch.fulfilled,
        (state, action: PayloadAction<Question[]>) => {
          state.data = action.payload;
          state.loading = false;
          state.error = null;
        }
      )
      .addCase(
        activitiesFetch.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.loading = false;
          state.error = action.payload || "Unknown error";
        }
      );
  },
});

export default activitiesSlice.reducer;
